import { createApi } from "@reduxjs/toolkit/query/react"
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { ReportRequest } from "shared/report"
import { REPORT_TRANSPORT_PARAM, REPORT_TRANSPORT_S3 } from "shared/reportData"
import { baseQuery } from "../baseQuerySl2"

/**
 * What `POST /report-data` returns since LOCKER-452: never the report, but an
 * origin-relative presigned path to it (`/sl2-report-data/...`), served from
 * S3 through a CloudFront behaviour on the host that served this page — so the
 * bytes come from a Mobilus domain, never `*.amazonaws.com`.
 */
export type ReportEnvelope = {
  url: string
  key: string
  expiresIn: number
  /** File exports only. */
  filename?: string
  contentType?: string
  /** File exports only, and only when the 20k-row cap left rows out. */
  truncation?: { exportedRows: number; totalRows: number }
}

/**
 * Whether a `POST /report-data` answer is an envelope. The tenant's
 * `ReportDataViaS3` flag decides server-side, so the page takes whichever
 * shape arrives: an envelope, or the pre-LOCKER-452 inline body (`{ res }`
 * for JSON, a base64 file otherwise).
 */
export const isReportEnvelope = (data: unknown): data is ReportEnvelope =>
  typeof (data as ReportEnvelope)?.url === "string" &&
  typeof (data as ReportEnvelope)?.key === "string"

/**
 * GET the report object an envelope points at. The URL is relative on
 * purpose, so the browser resolves it against this page's origin; the
 * signature is in the query string, so no auth header is sent.
 *
 * A host whose `/sl2-report-data/*` is NOT routed to the bucket (local Vite,
 * or a distribution missing the behaviour) answers with the SPA's
 * `index.html` as a 200. S3 always serves the type the presign forced, so any
 * other type is turned into a failed response here — otherwise an export
 * would silently download HTML under a `.csv` name.
 */
export const fetchReportObject = async (envelope: ReportEnvelope) => {
  const response = await fetch(envelope.url)
  const expected = envelope.contentType ?? "application/json"
  const actual = response.headers.get("Content-Type") ?? ""
  if (response.ok && !actual.startsWith(expected)) {
    return new Response(
      `report object came back as "${actual}", expected "${expected}" — ` +
        "is /sl2-report-data/* routed to the report bucket on this host?",
      { status: 502 },
    )
  }
  return response
}

/**
 * The `POST /report-data` path for a file export, opted in to the S3
 * envelope. Without the param the server always answers inline, which is
 * what a cached pre-LOCKER-452 WFE relies on.
 */
export const REPORT_DATA_EXPORT_PATH = `/report-data?${REPORT_TRANSPORT_PARAM}=${REPORT_TRANSPORT_S3}`

export type ExportFile = {
  blob: Blob
  filename: string
  truncation?: ReportEnvelope["truncation"]
}

/**
 * Turn a successful file-export `POST /report-data` response into the file,
 * whichever shape the tenant's flag produced. `null` when the file could not
 * be obtained (logged).
 */
export const resolveExportFile = async (
  response: Response,
): Promise<ExportFile | null> => {
  const contentType = response.headers.get("Content-Type") || ""

  if (contentType.startsWith("application/json")) {
    const envelope = (await response.json()) as ReportEnvelope
    const fileResponse = await fetchReportObject(envelope)
    if (!fileResponse.ok) {
      console.error("Error downloading report data file", fileResponse.status)
      return null
    }
    return {
      blob: new Blob([await fileResponse.arrayBuffer()], {
        type: envelope.contentType || "text/csv",
      }),
      filename: envelope.filename || "data.txt",
      truncation: envelope.truncation,
    }
  }

  // Pre-LOCKER-452 inline file: base64 body, name in Content-Disposition.
  const disposition = response.headers.get("Content-Disposition") || ""
  const filename = disposition.match(/filename="(.+)"/)?.[1] || "data.txt"
  const bytes = Uint8Array.from(window.atob(await response.text()), (c) =>
    c.charCodeAt(0),
  )
  return { blob: new Blob([bytes], { type: contentType || "text/csv" }), filename }
}

export const reportApi = createApi({
  reducerPath: "reportApi",
  baseQuery,
  endpoints: (builder) => ({
    requestReportData: builder.mutation<ReportRequest, any>({
      // Two hops, one mutation: the envelope, then the `{ res }` object it
      // points at — so every caller still receives the body it always did.
      queryFn: async ({ params = {}, body }, _api, _extra, fetchWithBQ) => {
        const envelope = await fetchWithBQ({
          url: "report-data",
          method: "POST",
          // Opt in to the S3 envelope; the tenant flag still decides.
          params: { ...params, [REPORT_TRANSPORT_PARAM]: REPORT_TRANSPORT_S3 },
          body,
        })
        if (envelope.error) return { error: envelope.error }
        // Flag off for this tenant: the body came inline, as it always did.
        if (!isReportEnvelope(envelope.data)) {
          return { data: envelope.data as ReportRequest }
        }

        const response = await fetchReportObject(envelope.data)
        if (!response.ok) {
          return {
            error: {
              status: response.status,
              data: await response.text(),
            } as FetchBaseQueryError,
          }
        }
        return { data: (await response.json()) as ReportRequest }
      },
    }),
  }),
})

export const { useRequestReportDataMutation } = reportApi
